import { sessionRequest } from 'app/models/auth/actions';
import OauthError from 'app/services/oauth/OauthError';
import { delegateOauth } from 'app/utils/api';
import { DelegateResponse, parseDelegateResponse } from 'app/services/oauth/DelegateResponse';
import { DelegatedTokenType } from 'app/services/oauth/constants';
import createErrorType from 'app/utils/createErrorType';
import { withOauthErrors } from 'app/models/request/oauth';
import { stringify } from 'query-string';
import { IAuthenticateRequest } from './AuthenticateRequest';
import { AppDispatch } from 'redux';

export class MissingRedirectURIError extends createErrorType('MissingRedirectURIError') {
}

export class UnexpectedTokenTypeError extends createErrorType('UnexpectedTokenTypeError') {
}

export const oauthSessionRequest = withOauthErrors(sessionRequest);

interface IDelegateResult {
  redirectTo: string;
}

export const delegate = (request: IAuthenticateRequest) => async (dispatch: AppDispatch): Promise<IDelegateResult> => {
  let delegateResult: DelegateResponse;
  try {
    delegateResult = parseDelegateResponse(
      await dispatch(oauthSessionRequest(delegateOauth(request))));
  } catch (e) {
    if (e instanceof OauthError) {
      const errorBody = e.oauthError;
      if (errorBody.error !== OauthError.Codes.INVALID_CLIENT &&
          errorBody.error !== OauthError.Codes.INVALID_REDIRECT_URI) {
        // The server promises to raise these errors with higher priority than
        // all others. So if we're here, the redirect URI is validated
        // and should be used.
        const errorRedirectURI = errorBody.redirect_uri || request.redirect_uri;
        if (!errorRedirectURI) {
          throw new MissingRedirectURIError('no redirect_uri available in request or response');
        }

        const errorReply = {
          error:             errorBody.error,
          error_description: errorBody.error_description,
          error_uri:         errorBody.error_uri,
          state:             request.state,
        };

        const redirectTo = `${errorRedirectURI}#${stringify(errorReply)}`;
        return { redirectTo };
      }
    }

    // fall through to unexpected error
    throw e;
  }

  const redirectURI = delegateResult.redirect_uri || request.redirect_uri;
  if (!redirectURI) {
    throw new MissingRedirectURIError('no redirect_uri available in request or response');
  }

  const rawTokenType: DelegatedTokenType = delegateResult.token_type;

  if (delegateResult.token_type === DelegatedTokenType.AUTHORIZATION_CODE) {
    const successReply = {
      code:  delegateResult.access_token,
      state: request.state,
    };

    const redirectTo = `${redirectURI}?${stringify(successReply)}`;
    return { redirectTo };
  } else if (delegateResult.token_type === DelegatedTokenType.BEARER) {
    const successReply = {
      token_type:   delegateResult.token_type,
      access_token: delegateResult.access_token,
      expires_in:   delegateResult.expires_in,
      scope:        delegateResult.scope,
      created_at:   delegateResult.created_at,
      state:        request.state,
    };

    const redirectTo = `${redirectURI}#${stringify(successReply)}`;
    return { redirectTo };
  }

  throw new UnexpectedTokenTypeError(`Unhandled token type: ${rawTokenType}`);
};
